import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BranchModel } from '@core/api/models/branch.model';
import { BranchApiService } from '@core/api/services/branch-api.service';
import { ReferralApiService } from '@core/api/services/referral-api-service';
import { UserReferralApiService } from '@core/api/services/user-referral-api-service';
import { Branch, BranchAdapter } from '@core/models/branch.model';
import { JointCommittee } from '@core/models/organisation/joint-committee.model';
import { Partner } from '@core/models/partner.model';
import { TranslateService } from '@ngx-translate/core';
import { adaptTimeForAPI } from '@shared/constants/time-adapt-function';
import { BehaviorSubject, firstValueFrom, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

@Injectable({
providedIn: 'root'
})

export class BranchService {

  vanityMode:boolean = false;
  branchSubject: BehaviorSubject<Branch|null> = new BehaviorSubject<Branch|null>(null);
  orderbranchSubject: BehaviorSubject<Branch|null> = new BehaviorSubject<Branch|null>(null);
  referral$: BehaviorSubject<Partner|null> = new BehaviorSubject<Partner|null>(null);
  standardbranch:Branch = {
    id:'',
    name:'Smart Lions',
    logo:'assets/img/default/smartlions.svg',
    background:'assets/img/default/bg.svg',
    color:'#0B145A',
    baseline:this.translate.instant('sl-baseline'),
    supportEmail:'support@smartlions.email',
    isPublic:true
//    catalogUrl:'https://smartlions.academy'
  } as Branch;  
  

  constructor(
    private branchAdapter:BranchAdapter, private branchApiService:BranchApiService, private route: ActivatedRoute, public translate:TranslateService,
    private referralApiService:ReferralApiService,
    private userReferralApiService:UserReferralApiService) {

    this.translate.onLangChange.subscribe(x =>{
      this.standardbranch.baseline = this.translate.instant('sl-baseline');
      if(this.branch && this.branch.id == this.standardbranch.id){
        this.branchSubject.next(this.standardbranch);
      }
    })
  }


  get branch(){
    return this.branchSubject.value;
  }

  get orderBranch(){
    return this.orderbranchSubject.value;
  }

  setOrderBranchById(id:string){
    this.branchApiService.get(id).subscribe(
      (branch:any) => {
        this.setOrderBranch(this.branchAdapter.adapt(branch));
      }
    );
  }
  
  setOrderBranch(branch:Branch){
    this.orderbranchSubject.next(branch);
    this.branchSubject.next(branch);
  }

  getOrderBranch(){
    return this.orderbranchSubject.asObservable();
  }

  getBranch(){
    return this.branchSubject.asObservable();
  }

  getBranchById(id:string){
    return this.branchApiService.get(id).pipe(map(
      (branch:BranchModel) => {
        return this.branchAdapter.adapt(branch);
      }
    ));
  }

  setReferral(id:string){
    if(id){
      this.referralApiService.get(id).subscribe(m => {
          const partner:Partner =  {id:m.id, name:m.name, image:m.logo,color:m.colour, background:m.background, packageId:(m.package??'').split('/').pop(),committees: (m.jointCommittees??[]).map((c:any) => {return {id:c.id, name:"PC"+c.number, description:c.description } as JointCommittee})}
          this.referral$.next(partner);
      })
    }
  }
  getReferral(id:string){    
    return this.referralApiService.get(id).pipe(map(m => {
           const partner:Partner =  {id:m.id, name:m.name, image:m.logo,color:m.colour, background:m.background, packageId:(m.package??'').split('/').pop(),committees: (m.jointCommittees??[]).map((c:any) => {return {id:c.id, name:"PC"+c.number, description:c.description } as JointCommittee})}
           return partner;
      }));
  }

  denyReferral(id:string){
    return this.userReferralApiService.update(id, {declinedAt:adaptTimeForAPI(new Date())})
  }

  clearReferral(){
    this.referral$.next(null);
  }

  lookUpReferral(referral:string){
    if(referral){
      this.referralApiService.list({urlParameter:referral}).subscribe(x => {

        if(x.totalItems > 0){
          const m = x.members[0];
          const partner:Partner =  {id:m.id, name:m.name, image:m.logo, color:m.colour, background:m.background, packageId:(m.package??'').split('/').pop(), committees: (m.jointCommittees??[]).map((c:any) => {return {id:c.id, name:"PC"+c.number, description:c.description } as JointCommittee})}
          this.referral$.next(partner);
        }
      })
    }
  }

  /**
   * Resolves the active branch for the current hostname and settles `vanityMode` BEFORE the app
   * finishes bootstrapping (wired as an APP_INITIALIZER). Awaiting it removes the race where guards
   * and components used to read the default `vanityMode = false` before detection completed.
   *
   * Falls back to the standard (non-vanity) branch on local/dev/platform hosts, on a no-match, and
   * on any API error/timeout — so `vanityMode` is always left in a deterministic state.
   */
  async autoDetectBranch(): Promise<void> {
    const hostname = window.location.hostname ?? '';
    const firstLabel = hostname.split('.').shift() ?? '';
    const exceptions = ['localhost', 'development', 'smartlions'];

    if (!hostname || exceptions.includes(firstLabel)) {
      this.vanityMode = false;
      this.branchSubject.next(this.standardbranch);
      return;
    }

    const branches = await firstValueFrom(
      this.branchApiService.getByVanityUrl(hostname).pipe(
        catchError(() => of(null)),
      ),
    );

    if (branches && branches.totalItems) {
      this.branchSubject.next(this.branchAdapter.adapt(branches.members[0]));
      this.vanityMode = true;
    } else {
      this.vanityMode = false;
      this.branchSubject.next(this.standardbranch);
    }
  }
}
